import { integer, object, optional, string } from './shape.js'
import type { Infer, OptionalShape } from './shape.js'

/**
 * How a list is asked for, and how one comes back.
 *
 * Every list endpoint on the platform pages the same way, and it pages by
 * cursor rather than by offset. That is a correctness decision and not a
 * performance one, so it is worth writing down properly.
 *
 * An offset page asks for "rows fifty to ninety-nine of whatever the answer is
 * right now", and the answer changes between the request for page one and the
 * request for page two. Somebody opens a case while a worker is reading: every
 * row below it shifts down one, so the row that was at position fifty is now
 * at fifty-one and appears on both pages. Somebody closes a case and it leaves
 * the filter: everything shifts up, and the row that was at fifty is never
 * shown at all. On a catalogue of products that is a cosmetic annoyance. On an
 * audit log it means an access that happened is missing from the export that
 * was taken to prove what happened, and on a case list it means a person's
 * case was skipped by the worker paging through their caseload. Neither of
 * those failures announces itself — the page looked full and the request
 * succeeded — which is exactly why it has to be designed out rather than
 * watched for.
 *
 * A cursor asks for "the rows after this particular one", so an insert or a
 * delete somewhere else in the set cannot move the boundary. Rows that appear
 * ahead of the reader are missed and rows that appear behind are not repeated,
 * which is the honest behaviour for a set that is changing while it is read.
 *
 * The costs are real and are accepted. There is no jumping to page seven, and
 * no total count arrives for free. Neither of those is worth a log that
 * quietly omits rows.
 */

/**
 * The page size used when the caller does not ask for one.
 *
 * Fifty is roughly a console table's first screen: enough that scrolling
 * usually does not need a second request, small enough that the screen is not
 * waiting on the hundredth row before it draws anything.
 */
export const DEFAULT_PAGE_LIMIT = 50

/**
 * The largest page anybody may ask for.
 *
 * A ceiling rather than an open limit, because `limit=100000` against the
 * audit log is a request to assemble a whole table in memory and serialise it
 * into a single response, and it costs the caller who sends it nothing at all.
 * An export that genuinely needs every row walks the cursor, which keeps the
 * work proportional to what has been read so far.
 */
export const MAX_PAGE_LIMIT = 200

/**
 * A sanity bound on a cursor, not a statement about its format.
 *
 * Long enough for a timestamp and an id with room to spare, short enough that
 * a cursor cannot be used to push a large string through a query parameter
 * into a log line.
 */
export const MAX_CURSOR_LENGTH = 256

/** The `limit` bounds, written down once so no endpoint can drift from them. */
export const pageLimit = integer({ min: 1, max: MAX_PAGE_LIMIT })

/**
 * What a caller sends to ask for a page.
 *
 * The `limit` validator is a parameter rather than a fixed field because the
 * same two fields arrive as JSON numbers from one caller and as query-string
 * text from another, and turning `'25'` into `25` is an HTTP concern that has
 * no business inside this package. The server passes a coercing limit; every
 * other caller takes the default.
 *
 * Both fields are optional. No cursor means the first page, which is what an
 * unadorned `GET /v1/org/cases` should mean.
 */
export function pageRequest(limit: OptionalShape<number> = optional(pageLimit)) {
  return object({
    limit,
    cursor: optional(string({ min: 1, max: MAX_CURSOR_LENGTH })),
  })
}

export type PageRequest = Infer<ReturnType<typeof pageRequest>>

/**
 * The envelope every list endpoint returns.
 *
 * One shape for all of them, and the rows always live under `items` rather
 * than under `cases` or `tasks`. A console that has to know a collection's
 * name before it can find the rows cannot have one paging helper, and twenty
 * hand-written ones are twenty chances to forget the cursor.
 *
 * Deliberately absent: a total count. Producing one means a second query over
 * the whole filtered set on every page, and the number is stale by the time it
 * is rendered anyway. An endpoint that genuinely needs a total says so
 * separately and pays for it on purpose.
 */
export interface Page<T> {
  /** This page, in the order the endpoint documents. Empty is `[]`, never absent. */
  items: T[]
  /**
   * Send back as `cursor` to get the next page. Absent means this was the last
   * one.
   *
   * This, and not an empty `items`, is the signal to stop. The two agree today
   * because of how `pageOf` is built, and a client that reads the wrong one
   * breaks the first time an endpoint filters rows out after reading them.
   */
  nextCursor?: string
}

/**
 * What a cursor has to carry, named once so that twenty endpoints agree.
 *
 * A cursor marks a position in a total order, which takes the sort key **and**
 * a tiebreak. The sort key alone is not enough: two cases opened in the same
 * millisecond compare equal, so `where opened_at < $cursor` either drops one
 * of them or returns both again on every page, forever, depending on which way
 * the comparison runs. The row id makes the order total, and the query becomes
 * `(opened_at, id) < ($at, $id)`.
 *
 * The encoding is deliberately not defined in this package. Only the server
 * ever looks inside a cursor — to a client it is a string it was handed and
 * sends back — so the codec belongs next to the queries that know what the
 * sort key is, and shipping one to a phone that will never call it is the sort
 * of weight this package exists to avoid. What is fixed here is the contract:
 * a cursor is opaque to the client; it is not a credential, because a caller
 * who unpacks one and edits it can only move around inside a set they were
 * already allowed to read, the tenant filter being a separate clause that no
 * cursor touches; and an unreadable cursor is a 400 rather than a silent
 * first page, which would look to the reader like the list restarting.
 */
export interface CursorPosition {
  /** The sort key of the last row on the page, as an ISO-8601 instant. */
  at: string
  /** That row's id, which settles the order when two rows share an instant. */
  id: string
}

/**
 * Builds the envelope from one row more than was asked for.
 *
 * The convention every list endpoint follows: select `limit + 1` rows, pass
 * the result here, and the extra row is dropped while the fact that it existed
 * becomes the cursor.
 *
 * Answering "is there a next page" any other way is worse. A second `count`
 * query doubles the database work per page and can still disagree with the
 * rows just read. The usual shortcut — return a cursor whenever the page came
 * back full — hands every caller one wasted round trip at the end of every
 * list, and on a set whose size is an exact multiple of the limit it hands
 * them an empty final page as well.
 */
export function pageOf<T>(
  rows: readonly T[],
  limit: number,
  cursorFor: (row: T) => string,
): Page<T> {
  const hasMore = rows.length > limit
  const items = rows.slice(0, limit)
  const last = items[items.length - 1]
  if (!hasMore || last === undefined) return { items }
  return { items, nextCursor: cursorFor(last) }
}
